import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * URS-F471: rollup enriquecido para los huecos de "Programacion" en Agenda.
 * Mismo patron de posicionamiento que ApptChip.HoverInfo (portal + fixed
 * con deteccion de bordes). Muestra:
 *  - Estudio (dot + code + title)
 *  - Visita (visit_code)
 *  - Fecha (dd/mm/aaaa)
 *  - Horario del slot (start_time - end_time)
 *  - Citas cubiertas (contador de appointments cuyo `visit_code` coincide
 *    y cuya hora cae dentro del rango del slot). Excluye anuladas.
 *  - Si el slot esta lockeado por otro usuario, se muestra un banner.
 */

function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(136,136,136,${alpha})`;
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function formatDateEs(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function ScheduleSlotHoverInfo({ slot, study, dayStr, appointments, lock, lang, anchorRect }) {
  const popRef = useRef(null);
  const [style, setStyle] = useState({ opacity: 0, position: 'fixed', pointerEvents: 'none', zIndex: 9999 });

  useEffect(() => {
    if (!popRef.current || !anchorRect) return;
    const pop = popRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top;
    if (anchorRect.top - pop.height - 6 > 0) top = anchorRect.top - pop.height - 6;
    else top = anchorRect.bottom + 6;
    let left = anchorRect.left;
    if (left + pop.width > vw - 12) left = vw - pop.width - 12;
    if (left < 8) left = 8;
    if (top + pop.height > vh - 8) top = vh - pop.height - 8;
    if (top < 8) top = 8;
    setStyle({ position: 'fixed', top, left, zIndex: 9999, pointerEvents: 'none', opacity: 1, transition: 'opacity 120ms ease-in' });
  }, [anchorRect]);

  const color = study?.color || '#6B7280';
  // Contar appointments cubiertas por este slot: mismo study_id + visit_code
  // en el dia, con hora dentro de [start_time, end_time). Excluye anuladas.
  const covered = (appointments || []).filter(a =>
    a.study_id === slot.study_id
    && a.date === dayStr
    && (a.visit_code || '').toUpperCase() === (slot.visit_code || '').toUpperCase()
    && a.status !== 'anulada'
    && a.time
    && a.time >= slot.start_time
    && a.time < slot.end_time
  ).length;

  return createPortal(
    <div
      ref={popRef}
      data-testid={`schedule-slot-hover-${slot.study_id}-${slot.visit_code}`}
      className="w-64 bg-white border border-stone-200 rounded-md shadow-xl overflow-hidden"
      style={style}
    >
      <div className="h-1.5" style={{ backgroundColor: color }} />
      <div className="p-3 space-y-2.5">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <span className="text-xs font-semibold text-stone-800 truncate">
            {study?.code || slot.study_id} — {study?.title || ''}
          </span>
        </div>

        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
          <span className="text-stone-400 font-medium">{lang === 'es' ? 'Visita' : 'Visit'}</span>
          <span className="text-stone-800 font-mono">{slot.visit_code || '—'}</span>

          <span className="text-stone-400 font-medium">{lang === 'es' ? 'Fecha' : 'Date'}</span>
          <span className="text-stone-800 font-mono">{formatDateEs(dayStr)}</span>

          <span className="text-stone-400 font-medium">{lang === 'es' ? 'Horario' : 'Time range'}</span>
          <span className="text-stone-800 font-mono">{slot.start_time} – {slot.end_time}</span>

          <span className="text-stone-400 font-medium">{lang === 'es' ? 'Citas cubiertas' : 'Covered appts'}</span>
          <span className="text-stone-800 font-semibold">
            {covered} {covered === 1
              ? (lang === 'es' ? 'cita' : 'appointment')
              : (lang === 'es' ? 'citas' : 'appointments')}
          </span>
        </div>

        {lock && (
          <div
            className="text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded px-2 py-1"
            style={{ borderLeft: `3px solid ${hexToRgba(color, 0.5)}` }}
          >
            🔒 {lang === 'es' ? 'Bloqueado por' : 'Locked by'} <span className="font-semibold">{lock.locked_by_name}</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * <ScheduleSlotCell />: reemplaza el <div> de celda de programacion en
 * DayView y WeekView. Encapsula el hover + evita duplicar HTML/logica.
 */
export function ScheduleSlotCell({
  slot, study, lock, isHour, onClick, dayStr,
  appointments = [], lang = 'es', borderWidth = 3,
  alwaysShowLabel = false,
  testId,
}) {
  const [hovered, setHovered] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const cellRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    if (!cellRef.current) return;
    setAnchorRect(cellRef.current.getBoundingClientRect());
    setHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    setAnchorRect(null);
  }, []);

  const color = study?.color || '#888';

  return (
    <div
      ref={cellRef}
      className={`relative overflow-hidden ${lock ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      style={{
        flex: 1,
        backgroundColor: hexToRgba(color, lock ? 0.08 : 0.15),
        borderLeft: `${borderWidth}px solid ${hexToRgba(color, lock ? 0.25 : 0.5)}`,
      }}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={testId}
    >
      {!lock && alwaysShowLabel && (
        <span
          className="absolute inset-0 flex items-center justify-center text-[8px] font-medium pointer-events-none select-none"
          style={{ color: hexToRgba(color, 0.35) }}
        >
          {slot.visit_code}
        </span>
      )}
      {!lock && !alwaysShowLabel && isHour && (
        <span
          className="absolute inset-0 flex items-center justify-center text-[9px] font-medium pointer-events-none select-none"
          style={{ color: hexToRgba(color, 0.45) }}
        >
          {slot.visit_code}
        </span>
      )}
      {lock && (
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" data-testid="schedule-cell-lock">
          <span className="text-stone-500 text-[10px]">🔒</span>
        </span>
      )}
      {hovered && anchorRect && (
        <ScheduleSlotHoverInfo
          slot={slot}
          study={study}
          dayStr={dayStr}
          appointments={appointments}
          lock={lock}
          lang={lang}
          anchorRect={anchorRect}
        />
      )}
    </div>
  );
}
